/**
 * Integration details data for all supported integrations
 * Based on v1 implementation from frontend/src/integrations/data/integrationDetails.ts
 */

import type { IntegrationDetailsMap } from '@/types/integrations'

export const integrationDetails: IntegrationDetailsMap = {
  formitable: {
    displayName: {
      en: 'Formitable',
      'nl-NL': 'Formitable',
    },
    description: {
      en: 'Connect to Formitable for restaurant reservation management',
      'nl-NL': 'Verbind met Formitable voor restaurant reserveringsbeheer',
    },
    category: {
      en: 'Reservations',
      'nl-NL': 'Reserveringen',
    },
    status: 'inactive',
    iconPath: '/images/formitableIcon.svg',
    configFields: [
      {
        key: 'api_key',
        label: { en: 'API Key', 'nl-NL': 'API Sleutel' },
        type: 'password',
        required: true,
        description: {
          en: 'Your Formitable API key for authentication',
          'nl-NL': 'Uw Formitable API sleutel voor authenticatie',
        },
      },
      {
        key: 'restaurant_id',
        label: { en: 'Restaurant ID', 'nl-NL': 'Restaurant ID' },
        type: 'text',
        required: true,
        description: {
          en: 'Your unique restaurant identifier in Formitable. You can find it in https://app.formitable.com/settings/widgets',
          'nl-NL':
            'Uw unieke restaurant identificatie in Formitable. U kunt het vinden op https://app.formitable.com/settings/widgets',
        },
      },
      {
        key: 'max_party_size',
        label: { en: 'Max party size', 'nl-NL': 'Max groepsgrootte' },
        type: 'number',
        required: true,
        defaultValue: 8,
        description: {
          en: 'Maximum number of people allowed for online bookings. When exceeded, directs customers to contact the restaurant directly',
          'nl-NL':
            'Maximum aantal personen toegestaan voor online boekingen. Bij overschrijding worden klanten doorverwezen om het restaurant direct te contacteren',
        },
      },
      {
        key: 'option_expiration_minutes',
        label: { en: 'Option expiration', 'nl-NL': 'Optie vervaltijd' },
        type: 'select',
        required: false,
        defaultValue: 120,
        options: [
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
          { value: 240, label: { en: '4 hours', 'nl-NL': '4 uur' } },
          { value: 360, label: { en: '6 hours', 'nl-NL': '6 uur' } },
          { value: 720, label: { en: '12 hours', 'nl-NL': '12 uur' } },
          { value: 1440, label: { en: '24 hours', 'nl-NL': '24 uur' } },
        ],
        description: {
          en: 'How long booking options remain valid before expiring',
          'nl-NL': 'Hoe lang boekingsopties geldig blijven voordat ze verlopen',
        },
      },
      {
        key: 'send_option_reminders',
        label: { en: 'Send option reminders', 'nl-NL': 'Verstuur optie herinneringen' },
        type: 'boolean',
        required: false,
        defaultValue: false,
        description: {
          en: 'Automatically send reminder messages about pending reservation options',
          'nl-NL':
            'Verstuur automatisch herinneringsberichten over openstaande reserveringsopties',
        },
      },
      {
        key: 'enable_messaging',
        label: { en: 'Enable messaging', 'nl-NL': 'Berichten inschakelen' },
        type: 'boolean',
        required: false,
        defaultValue: true,
        description: {
          en: 'Allow sending messages to restaurant staff about special requests or arrival updates',
          'nl-NL':
            'Sta toe berichten te sturen naar restaurantpersoneel over speciale verzoeken of aankomst updates',
        },
      },
      {
        key: 'allowed_delay_minutes',
        label: { en: 'Allowed delay', 'nl-NL': 'Toegestane vertraging' },
        type: 'select',
        required: false,
        options: [
          { value: 0, label: { en: 'No delay', 'nl-NL': 'Geen vertraging' } },
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 45, label: { en: '45 minutes', 'nl-NL': '45 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
        ],
        description: {
          en: 'Allows guests to arrive late without needing to update their reservation',
          'nl-NL': 'Stelt gasten in staat laat aan te komen zonder hun reservering bij te werken',
        },
      },
      {
        key: 'no_deposit_required_minutes',
        label: { en: 'No deposit required window', 'nl-NL': 'Geen aanbetaling vereist venster' },
        type: 'select',
        required: false,
        options: [
          { value: 0, label: { en: 'No window', 'nl-NL': 'Geen venster' } },
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
          { value: 360, label: { en: '6 hours', 'nl-NL': '6 uur' } },
          { value: 720, label: { en: '12 hours', 'nl-NL': '12 uur' } },
          { value: 1440, label: { en: '24 hours', 'nl-NL': '24 uur' } },
          { value: 2880, label: { en: '48 hours', 'nl-NL': '48 uur' } },
        ],
        description: {
          en: 'Time window before booking where deposits are waived',
          'nl-NL': 'Tijdvenster voor boeking waarbij aanbetalingen worden kwijtgescholden',
        },
      },
      {
        key: 'ignore_update_restrictions',
        label: { en: 'Ignore update restrictions', 'nl-NL': 'Negeer update beperkingen' },
        type: 'radio',
        required: false,
        defaultValue: 0,
        options: [
          { value: 0, label: { en: 'Never', 'nl-NL': 'Nooit' } },
          { value: 1, label: { en: 'Only Non-Prepaid', 'nl-NL': 'Alleen Niet-Vooruitbetaald' } },
          { value: 2, label: { en: 'Always', 'nl-NL': 'Altijd' } },
        ],
        description: {
          en: 'Controls whether to bypass normal booking update restrictions',
          'nl-NL': 'Bepaalt of normale boekingsupdate beperkingen worden omzeild',
        },
      },
      {
        key: 'availability_days_in_context',
        label: { en: 'Availability days in context', 'nl-NL': 'Beschikbaarheidsdagen in context' },
        type: 'number',
        required: false,
        defaultValue: 30,
        description: {
          en: 'Number of days ahead to include unavailable dates in conversation context',
          'nl-NL': 'Aantal dagen vooruit om niet-beschikbare datums op te nemen in gesprekcontext',
        },
      },
      {
        key: 'no_availability_transfer_window',
        label: {
          en: 'Forwarding to the restaurant when no tables are available',
          'nl-NL': 'Doorschakelen naar het restaurant wanneer er geen tafels beschikbaar zijn',
        },
        type: 'select',
        required: false,
        defaultValue: 0,
        options: [
          { value: 0, label: { en: 'Disabled', 'nl-NL': 'Uitgeschakeld' } },
          { value: 10, label: { en: '10 minutes', 'nl-NL': '10 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
          { value: 240, label: { en: '4 hours', 'nl-NL': '4 uur' } },
          { value: 360, label: { en: '6 hours', 'nl-NL': '6 uur' } },
        ],
        description: {
          en: "If Bonnie can't find any tables around the requested time, the call will be forwarded",
          'nl-NL':
            'Als Bonnie geen tafels kan vinden rond de gevraagde tijd, wordt het gesprek doorgeschakeld',
        },
      },
      {
        key: 'cross_sell_assistant_id',
        label: { en: 'Cross-sell assistant ID', 'nl-NL': 'Cross-sell assistent ID' },
        type: 'number',
        required: false,
        adminOnly: true,
        description: {
          en: 'ID of the assistant to use for cross-sell (another location)',
          'nl-NL': 'ID van de assistent voor cross-sell (andere locatie)',
        },
      },
      {
        key: 'cross_sell_time_gap_minutes',
        label: { en: 'Cross-sell time gap', 'nl-NL': 'Cross-sell tijdvenster' },
        type: 'select',
        required: false,
        defaultValue: 120,
        adminOnly: true,
        options: [
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
          { value: 240, label: { en: '4 hours', 'nl-NL': '4 uur' } },
          { value: 300, label: { en: '5 hours', 'nl-NL': '5 uur' } },
          { value: 360, label: { en: '6 hours', 'nl-NL': '6 uur' } },
        ],
        description: {
          en: 'Time gap in minutes around requested time to check for cross-sell trigger',
          'nl-NL': 'Tijdvenster in minuten rond de gevraagde tijd om cross-sell te triggeren',
        },
      },
    ],
  },
  zenchef: {
    displayName: { en: 'Zenchef', 'nl-NL': 'Zenchef' },
    description: {
      en: 'Connect to Zenchef for restaurant reservation management',
      'nl-NL': 'Verbind met Zenchef voor restaurant reserveringsbeheer',
    },
    category: { en: 'Reservations', 'nl-NL': 'Reserveringen' },
    status: 'live',
    iconPath: '/images/zenchefIcon.svg',
    configFields: [
      {
        key: 'restaurant_id',
        label: { en: 'Restaurant ID', 'nl-NL': 'Restaurant ID' },
        type: 'text',
        required: true,
        description: {
          en: 'Your unique restaurant identifier in Zenchef. You can find it in https://app.zenchef.com/zw#/parameters/partners',
          'nl-NL':
            'Uw unieke restaurant identificatie in Zenchef. U kunt het vinden op https://app.zenchef.com/zw#/parameters/partners',
        },
      },
      {
        key: 'max_party_size',
        label: { en: 'Max party size', 'nl-NL': 'Max groepsgrootte' },
        type: 'number',
        required: true,
        description: {
          en: 'Maximum number of people allowed for online bookings',
          'nl-NL': 'Maximum aantal personen toegestaan voor online boekingen',
        },
      },
      {
        key: 'enable_messaging',
        label: { en: 'Enable messaging', 'nl-NL': 'Berichten inschakelen' },
        type: 'boolean',
        required: false,
        defaultValue: true,
        description: {
          en: 'Allow sending messages to restaurant staff',
          'nl-NL': 'Sta toe berichten te sturen naar restaurantpersoneel',
        },
      },
      {
        key: 'guest_profile',
        label: { en: 'Use guest profile', 'nl-NL': 'Gebruik gastprofiel' },
        type: 'boolean',
        required: false,
        defaultValue: true,
        description: {
          en: 'Retrieve and use existing guest profile information',
          'nl-NL': 'Haal bestaande gastprofielinformatie op en gebruik deze',
        },
      },
      {
        key: 'availability_days_in_context',
        label: { en: 'Availability days in context', 'nl-NL': 'Beschikbaarheidsdagen in context' },
        type: 'number',
        required: false,
        defaultValue: 30,
        description: {
          en: 'Number of days ahead to include unavailable dates in conversation context',
          'nl-NL': 'Aantal dagen vooruit om niet-beschikbare datums op te nemen',
        },
      },
      {
        key: 'no_availability_transfer_window',
        label: {
          en: 'Forwarding when no tables available',
          'nl-NL': 'Doorschakelen wanneer geen tafels beschikbaar',
        },
        type: 'select',
        required: false,
        defaultValue: 0,
        options: [
          { value: 0, label: { en: 'Disabled', 'nl-NL': 'Uitgeschakeld' } },
          { value: 10, label: { en: '10 minutes', 'nl-NL': '10 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
          { value: 240, label: { en: '4 hours', 'nl-NL': '4 uur' } },
          { value: 360, label: { en: '6 hours', 'nl-NL': '6 uur' } },
        ],
        description: {
          en: "Forward to restaurant when Bonnie can't find any tables",
          'nl-NL': 'Schakel door naar restaurant als Bonnie geen tafels kan vinden',
        },
      },
      {
        key: 'allowed_delay_minutes',
        label: { en: 'Allowed delay', 'nl-NL': 'Toegestane vertraging' },
        type: 'select',
        required: false,
        options: [
          { value: 0, label: { en: 'No delay', 'nl-NL': 'Geen vertraging' } },
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 45, label: { en: '45 minutes', 'nl-NL': '45 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
        ],
        description: {
          en: 'Allows guests to arrive late without updating reservation',
          'nl-NL': 'Stelt gasten in staat laat aan te komen',
        },
      },
      {
        key: 'imminent_window_minutes',
        label: { en: 'Imminent window', 'nl-NL': 'Boekingen op korte termijn' },
        type: 'select',
        required: false,
        defaultValue: 120,
        options: [
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 45, label: { en: '45 minutes', 'nl-NL': '45 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
        ],
        description: {
          en: 'Defines what constitutes an "imminent" booking for special handling',
          'nl-NL': 'Definieert het tijdsinterval waarin een boeking als binnenkort wordt beschouwd',
        },
      },
      {
        key: 'no_deposit_required_minutes',
        label: { en: 'No deposit required window', 'nl-NL': 'Geen aanbetaling vereist venster' },
        type: 'select',
        required: false,
        options: [
          { value: 0, label: { en: 'No window', 'nl-NL': 'Geen venster' } },
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
          { value: 360, label: { en: '6 hours', 'nl-NL': '6 uur' } },
          { value: 720, label: { en: '12 hours', 'nl-NL': '12 uur' } },
          { value: 1440, label: { en: '24 hours', 'nl-NL': '24 uur' } },
          { value: 2880, label: { en: '48 hours', 'nl-NL': '48 uur' } },
        ],
        description: {
          en: 'Time window before booking where deposits are waived',
          'nl-NL': 'Tijdvenster voor boeking waarbij aanbetalingen worden kwijtgescholden',
        },
      },
      {
        key: 'ignore_update_restrictions',
        label: { en: 'Ignore update restrictions', 'nl-NL': 'Negeer update beperkingen' },
        type: 'radio',
        required: false,
        defaultValue: 0,
        options: [
          { value: 0, label: { en: 'Never', 'nl-NL': 'Nooit' } },
          { value: 1, label: { en: 'Only Non-Prepaid', 'nl-NL': 'Alleen Niet-Vooruitbetaald' } },
          { value: 2, label: { en: 'Always', 'nl-NL': 'Altijd' } },
        ],
        description: {
          en: 'Controls whether to bypass normal booking update restrictions',
          'nl-NL': 'Bepaalt of normale boekingsupdate beperkingen worden omzeild',
        },
      },
      {
        key: 'room_selection_enabled',
        label: { en: 'Room selection enabled', 'nl-NL': 'Ruimtekeuze ingeschakeld' },
        required: false,
        type: 'boolean',
        defaultValue: false,
        description: {
          en: 'Enables guests or staff to choose a specific room during booking',
          'nl-NL':
            'Maakt het mogelijk voor gasten of personeel om een specifieke ruimte te kiezen',
        },
      },
      {
        key: 'disable_whatsapp_for_prepayment',
        label: { en: 'Disable WhatsApp for Prepayment', 'nl-NL': 'WhatsApp Uitschakelen voor Vooruitbetaling' },
        type: 'boolean',
        required: false,
        defaultValue: false,
        description: {
          en: 'Prevents WhatsApp messages for bookings requiring deposits',
          'nl-NL': 'Voorkomt WhatsApp berichten voor boekingen die aanbetalingen vereisen',
        },
      },
      {
        key: 'disable_whatsapp_for_confirmation',
        label: { en: 'Disable WhatsApp for Confirmation', 'nl-NL': 'WhatsApp Uitschakelen voor Bevestiging' },
        type: 'boolean',
        required: false,
        defaultValue: false,
        description: {
          en: 'Prevents WhatsApp confirmation messages entirely',
          'nl-NL': 'Voorkomt WhatsApp bevestigingsberichten volledig',
        },
      },
    ],
  },
  fork: {
    displayName: { en: 'TheFork', 'nl-NL': 'TheFork' },
    description: {
      en: 'Connect to TheFork for restaurant reservation management',
      'nl-NL': 'Verbind met TheFork voor restaurant reserveringsbeheer',
    },
    category: { en: 'Reservations', 'nl-NL': 'Reserveringen' },
    status: 'live',
    iconPath: '/images/theforkIcon.svg',
    configFields: [
      {
        key: 'restaurant_id',
        label: { en: 'Restaurant ID', 'nl-NL': 'Restaurant ID' },
        type: 'text',
        required: true,
        description: {
          en: 'Your unique restaurant identifier in TheFork system',
          'nl-NL': 'Uw unieke restaurant identificatie in het TheFork systeem',
        },
      },
      {
        key: 'max_party_size',
        label: { en: 'Max party size', 'nl-NL': 'Max groepsgrootte' },
        type: 'number',
        required: true,
        description: {
          en: 'Maximum number of people allowed for online bookings',
          'nl-NL': 'Maximum aantal personen toegestaan voor online boekingen',
        },
      },
      {
        key: 'late_arrival_enabled',
        label: { en: 'Enable late arrival', 'nl-NL': 'Late aankomst inschakelen' },
        type: 'boolean',
        required: false,
        defaultValue: false,
        description: {
          en: 'Enable or disable the late arrival handling tool for guests',
          'nl-NL': 'Schakel afhandeling van late aankomst voor gasten in',
        },
      },
      {
        key: 'allowed_delay_minutes',
        label: { en: 'Allowed delay', 'nl-NL': 'Toegestane vertraging' },
        type: 'select',
        required: false,
        options: [
          { value: 0, label: { en: 'No delay', 'nl-NL': 'Geen vertraging' } },
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 45, label: { en: '45 minutes', 'nl-NL': '45 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
        ],
        description: {
          en: 'Allows guests to arrive late without updating reservation',
          'nl-NL': 'Stelt gasten in staat laat aan te komen',
        },
      },
      {
        key: 'no_availability_transfer_window',
        label: {
          en: 'Forwarding when no tables available',
          'nl-NL': 'Doorschakelen wanneer geen tafels beschikbaar',
        },
        type: 'select',
        required: false,
        defaultValue: 0,
        options: [
          { value: 0, label: { en: 'Disabled', 'nl-NL': 'Uitgeschakeld' } },
          { value: 10, label: { en: '10 minutes', 'nl-NL': '10 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
          { value: 240, label: { en: '4 hours', 'nl-NL': '4 uur' } },
          { value: 360, label: { en: '6 hours', 'nl-NL': '6 uur' } },
        ],
        description: {
          en: "Forward to restaurant when Bonnie can't find any tables",
          'nl-NL': 'Schakel door naar restaurant als Bonnie geen tafels kan vinden',
        },
      },
    ],
  },
  guestplan: {
    displayName: { en: 'Guestplan', 'nl-NL': 'Guestplan' },
    description: {
      en: 'Connect to Guestplan for restaurant reservation management',
      'nl-NL': 'Verbind met Guestplan voor restaurant reserveringsbeheer',
    },
    category: { en: 'Reservations', 'nl-NL': 'Reserveringen' },
    status: 'inactive',
    iconPath: '/images/guestplanIcon.svg',
    configFields: [
      {
        key: 'restaurant_id',
        label: { en: 'Restaurant ID', 'nl-NL': 'Restaurant ID' },
        type: 'text',
        required: true,
        description: {
          en: 'Your unique restaurant identifier in Guestplan',
          'nl-NL': 'Uw unieke restaurant identificatie in Guestplan',
        },
      },
      {
        key: 'api_key',
        label: { en: 'API key', 'nl-NL': 'API sleutel' },
        type: 'password',
        required: false,
        description: {
          en: 'Your Guestplan API key for authentication',
          'nl-NL': 'Uw Guestplan API sleutel voor authenticatie',
        },
      },
      {
        key: 'public_key',
        label: { en: 'Public key', 'nl-NL': 'Publieke sleutel' },
        type: 'text',
        required: false,
        description: {
          en: 'Public key for generating booking confirmation links',
          'nl-NL': 'Publieke sleutel voor het genereren van boekingsbevestigingslinks',
        },
      },
      {
        key: 'max_party_size',
        label: { en: 'Max party size', 'nl-NL': 'Max groepsgrootte' },
        type: 'number',
        required: false,
        defaultValue: 8,
        description: {
          en: 'Maximum number of people allowed for online bookings',
          'nl-NL': 'Maximum aantal personen toegestaan voor online boekingen',
        },
      },
      {
        key: 'connect_to_staff_minutes',
        label: { en: 'Connect to staff window', 'nl-NL': 'Verbind met personeel venster' },
        type: 'select',
        required: false,
        options: [
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
          { value: 360, label: { en: '6 hours', 'nl-NL': '6 uur' } },
          { value: 720, label: { en: '12 hours', 'nl-NL': '12 uur' } },
          { value: 1440, label: { en: '24 hours', 'nl-NL': '24 uur' } },
        ],
        description: {
          en: 'Minutes before booking when system should transfer calls to staff',
          'nl-NL': 'Minuten voor boeking wanneer het systeem gesprekken naar personeel moet doorschakelen',
        },
      },
      {
        key: 'enable_find_bookings',
        label: { en: 'Find bookings by phone', 'nl-NL': 'Boekingen zoeken op basis van telefoonnummer' },
        type: 'boolean',
        required: false,
        defaultValue: true,
        description: {
          en: 'Enable searching reservations by phone number, pre-call lookup, and resending booking confirmations',
          'nl-NL': 'Schakel zoeken van reserveringen op telefoonnummer, pre-call opzoeken en opnieuw verzenden van boekingsbevestigingen in',
        },
      },
      {
        key: 'late_arrival_enabled',
        label: { en: 'Enable late arrival', 'nl-NL': 'Late aankomst inschakelen' },
        type: 'boolean',
        required: false,
        defaultValue: false,
        description: {
          en: 'Enable or disable the late arrival handling tool for guests',
          'nl-NL': 'Schakel afhandeling van late aankomst voor gasten in',
        },
      },
      {
        key: 'allowed_delay_minutes',
        label: { en: 'Allowed delay', 'nl-NL': 'Toegestane vertraging' },
        type: 'select',
        required: false,
        options: [
          { value: 0, label: { en: 'No delay', 'nl-NL': 'Geen vertraging' } },
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 45, label: { en: '45 minutes', 'nl-NL': '45 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
        ],
        description: {
          en: 'Allows guests to arrive late without needing to update their reservation',
          'nl-NL': 'Stelt gasten in staat laat aan te komen zonder hun reservering bij te werken',
        },
      },
      {
        key: 'no_availability_transfer_window',
        label: {
          en: 'Forwarding when no tables available',
          'nl-NL': 'Doorschakelen wanneer geen tafels beschikbaar',
        },
        type: 'select',
        required: false,
        defaultValue: 0,
        options: [
          { value: 0, label: { en: 'Disabled', 'nl-NL': 'Uitgeschakeld' } },
          { value: 10, label: { en: '10 minutes', 'nl-NL': '10 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
          { value: 240, label: { en: '4 hours', 'nl-NL': '4 uur' } },
          { value: 360, label: { en: '6 hours', 'nl-NL': '6 uur' } },
        ],
        description: {
          en: "Forward to restaurant when Bonnie can't find any tables",
          'nl-NL': 'Schakel door naar restaurant als Bonnie geen tafels kan vinden',
        },
      },
    ],
  },
  tablefever: {
    displayName: { en: 'TableFever', 'nl-NL': 'TableFever' },
    description: {
      en: 'Connect to TableFever for restaurant reservation management',
      'nl-NL': 'Verbind met TableFever voor restaurant reserveringsbeheer',
    },
    category: { en: 'Reservations', 'nl-NL': 'Reserveringen' },
    status: 'inactive',
    iconPath: '/images/tablefeverIcon.svg',
    configFields: [
      {
        key: 'restaurant_id',
        label: { en: 'Restaurant ID', 'nl-NL': 'Restaurant ID' },
        type: 'text',
        required: true,
        description: {
          en: 'TableFever restaurant ID used for booking operations',
          'nl-NL': 'TableFever restaurant-ID gebruikt voor boekingen',
        },
      },
      {
        key: 'max_party_size',
        label: { en: 'Max party size', 'nl-NL': 'Max groepsgrootte' },
        type: 'number',
        required: false,
        defaultValue: 8,
        description: {
          en: 'Maximum number of people allowed for online bookings',
          'nl-NL': 'Maximum aantal personen toegestaan voor online boekingen',
        },
      },
      {
        key: 'late_arrival_enabled',
        label: { en: 'Enable late arrival', 'nl-NL': 'Late aankomst inschakelen' },
        type: 'boolean',
        required: false,
        defaultValue: false,
        description: {
          en: 'Enable or disable the late arrival handling tool for guests',
          'nl-NL': 'Schakel afhandeling van late aankomst voor gasten in',
        },
      },
      {
        key: 'allowed_delay_minutes',
        label: { en: 'Allowed delay', 'nl-NL': 'Toegestane vertraging' },
        type: 'select',
        required: false,
        options: [
          { value: 0, label: { en: 'No delay', 'nl-NL': 'Geen vertraging' } },
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 45, label: { en: '45 minutes', 'nl-NL': '45 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
        ],
        description: {
          en: 'Allows guests to arrive late without needing to update their reservation',
          'nl-NL': 'Stelt gasten in staat laat aan te komen zonder hun reservering bij te werken',
        },
      },
    ],
  },
  gotable: {
    displayName: { en: 'GoTable', 'nl-NL': 'GoTable' },
    description: {
      en: 'Connect to GoTable for restaurant reservation management',
      'nl-NL': 'Verbind met GoTable voor restaurant reserveringsbeheer',
    },
    category: { en: 'Reservations', 'nl-NL': 'Reserveringen' },
    status: 'live',
    iconPath: '/images/gotableIcon.svg',
    configFields: [
      {
        key: 'api_key',
        label: { en: 'API Key', 'nl-NL': 'API Sleutel' },
        type: 'password',
        required: false,
        description: {
          en: 'Your GoTable API key (Bearer token) - can stay empty if using Bonnie API key',
          'nl-NL':
            'Uw GoTable API sleutel (Bearer token) - kan leeg blijven indien Bonnie API sleutel wordt gebruikt',
        },
      },
      {
        key: 'restaurant_id',
        label: { en: 'Restaurant ID', 'nl-NL': 'Restaurant ID' },
        type: 'text',
        required: true,
        description: {
          en: 'Your unique restaurant identifier in GoTable',
          'nl-NL': 'Uw unieke restaurant identificatie in GoTable',
        },
      },
      {
        key: 'max_party_size',
        label: { en: 'Max party size', 'nl-NL': 'Max groepsgrootte' },
        type: 'number',
        required: false,
        defaultValue: 8,
        description: {
          en: 'Maximum number of people allowed for online bookings. When exceeded, directs customers to contact the restaurant directly',
          'nl-NL':
            'Maximum aantal personen toegestaan voor online boekingen. Bij overschrijding worden klanten doorverwezen om het restaurant direct te contacteren',
        },
      },
      {
        key: 'late_arrival_enabled',
        label: { en: 'Enable late arrival', 'nl-NL': 'Late aankomst inschakelen' },
        type: 'boolean',
        required: false,
        defaultValue: false,
        description: {
          en: 'Enable or disable the late arrival handling tool for guests',
          'nl-NL': 'Schakel afhandeling van late aankomst voor gasten in',
        },
      },
      {
        key: 'allowed_delay_minutes',
        label: { en: 'Allowed delay', 'nl-NL': 'Toegestane vertraging' },
        type: 'select',
        required: false,
        options: [
          { value: 0, label: { en: 'No delay', 'nl-NL': 'Geen vertraging' } },
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 45, label: { en: '45 minutes', 'nl-NL': '45 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
        ],
        description: {
          en: 'Allows guests to arrive late without needing to update their reservation',
          'nl-NL': 'Stelt gasten in staat laat aan te komen zonder hun reservering bij te werken',
        },
      },
    ],
  },
  frank: {
    displayName: { en: 'Frank', 'nl-NL': 'Frank' },
    description: {
      en: 'Connect to Frank for restaurant reservation management',
      'nl-NL': 'Verbind met Frank voor restaurant reserveringsbeheer',
    },
    category: { en: 'Reservations', 'nl-NL': 'Reserveringen' },
    status: 'live',
    iconPath: '/images/frankIcon.png',
    configFields: [
      {
        key: 'restaurant_id',
        label: { en: 'Restaurant ID', 'nl-NL': 'Restaurant ID' },
        type: 'text',
        required: true,
        pattern: '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$',
        validationMessage: {
          en: 'Enter a valid restaurant UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx).',
          'nl-NL': 'Voer een geldige restaurant-UUID in (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx).',
        },
        description: {
          en: 'Open your restaurant in the Frank dashboard, go to Setup → Integrations, and select Copy beside Restaurant ID. Enable Bonnie for the restaurant and in the channel settings of each shift you want Bonnie to offer.',
          'nl-NL': 'Open uw restaurant in het Frank-dashboard, ga naar Setup → Integrations en selecteer Copy naast Restaurant ID. Activeer Bonnie voor het restaurant en in de kanaalinstellingen van elke shift die Bonnie moet aanbieden.',
        },
      },
      {
        key: 'max_party_size',
        label: { en: 'Maximum party size', 'nl-NL': 'Maximale groepsgrootte' },
        type: 'number',
        required: false,
        min: 1,
        step: 1,
        validationMessage: {
          en: 'Enter a whole number of at least 1, or leave blank.',
          'nl-NL': 'Voer een geheel getal van minimaal 1 in, of laat leeg.',
        },
        description: {
          en: 'Largest group Bonnie may book. Leave blank for no configured limit; Frank availability still applies.',
          'nl-NL': 'Grootste groep waarvoor Bonnie mag reserveren. Laat leeg voor geen ingestelde limiet; beschikbaarheid in Frank blijft van toepassing.',
        },
      },
      {
        key: 'no_availability_transfer_window',
        label: { en: 'No-availability transfer window (minutes)', 'nl-NL': 'Doorverbindvenster bij geen beschikbaarheid (minuten)' },
        type: 'number',
        required: false,
        min: 0,
        max: 360,
        step: 1,
        validationMessage: {
          en: 'Enter a whole number from 0 to 360, or leave blank.',
          'nl-NL': 'Voer een geheel getal van 0 tot en met 360 in, of laat leeg.',
        },
        description: {
          en: 'Suggest connecting to staff when no availability is found within this window. Applies only to today in the assistant’s timezone. Set to 0 or leave blank to disable.',
          'nl-NL': 'Stel voor om door te verbinden met een medewerker wanneer binnen dit venster geen beschikbaarheid is gevonden. Geldt alleen voor vandaag in de tijdzone van de assistent. Stel in op 0 of laat leeg om uit te schakelen.',
        },
      },
      {
        key: 'late_arrival_enabled',
        label: { en: 'Enable late-arrival handling', 'nl-NL': 'Afhandeling van te laat komen inschakelen' },
        type: 'boolean',
        required: false,
        defaultValue: false,
        description: {
          en: 'Allow Bonnie to handle late arrivals for an identified reservation. Disabled by default.',
          'nl-NL': 'Laat Bonnie te laat komen afhandelen voor een gevonden reservering. Standaard uitgeschakeld.',
        },
      },
      {
        key: 'allowed_delay_minutes',
        label: { en: 'Late-arrival grace period (minutes)', 'nl-NL': 'Speling bij te laat komen (minuten)' },
        type: 'number',
        required: false,
        min: 0,
        max: 120,
        step: 1,
        validationMessage: {
          en: 'Enter a whole number from 0 to 120, or leave blank.',
          'nl-NL': 'Voer een geheel getal van 0 tot en met 120 in, of laat leeg.',
        },
        description: {
          en: 'Only applies when late-arrival handling is enabled. Within this delay, Bonnie adds a guest message without moving the reservation. Longer delays trigger a later-slot search. Set to 0 or leave blank to always search for a later slot.',
          'nl-NL': 'Geldt alleen als afhandeling van te laat komen is ingeschakeld. Binnen deze vertraging voegt Bonnie een gastbericht toe zonder de reservering te verplaatsen. Bij meer vertraging zoekt Bonnie een later tijdstip. Stel in op 0 of laat leeg om altijd een later tijdstip te zoeken.',
        },
      },
    ],
  },
  opentable: {
    displayName: { en: 'OpenTable', 'nl-NL': 'OpenTable' },
    description: {
      en: 'Connect to OpenTable for restaurant reservation management',
      'nl-NL': 'Verbind met OpenTable voor restaurant reserveringsbeheer',
    },
    category: { en: 'Reservations', 'nl-NL': 'Reserveringen' },
    status: 'live',
    iconPath: '/images/opentableIcon.svg',
    configFields: [
      {
        key: 'max_party_size',
        label: { en: 'Max party size', 'nl-NL': 'Max groepsgrootte' },
        type: 'number',
        required: false,
        defaultValue: 20,
        description: {
          en: 'Maximum number of people allowed for a booking (1-20)',
          'nl-NL': 'Maximum aantal personen toegestaan voor een boeking (1-20)',
        },
      },
      {
        key: 'allowed_delay_minutes',
        label: { en: 'Allowed delay', 'nl-NL': 'Toegestane vertraging' },
        type: 'select',
        required: false,
        options: [
          { value: 0, label: { en: 'No delay', 'nl-NL': 'Geen vertraging' } },
          { value: 15, label: { en: '15 minutes', 'nl-NL': '15 minuten' } },
          { value: 30, label: { en: '30 minutes', 'nl-NL': '30 minuten' } },
          { value: 45, label: { en: '45 minutes', 'nl-NL': '45 minuten' } },
          { value: 60, label: { en: '1 hour', 'nl-NL': '1 uur' } },
          { value: 90, label: { en: '1.5 hours', 'nl-NL': '1,5 uur' } },
          { value: 120, label: { en: '2 hours', 'nl-NL': '2 uur' } },
          { value: 180, label: { en: '3 hours', 'nl-NL': '3 uur' } },
        ],
        description: {
          en: 'Allows guests to arrive late without needing to update their reservation',
          'nl-NL': 'Stelt gasten in staat laat aan te komen zonder hun reservering bij te werken',
        },
      },
      {
        key: 'match_widget_availability',
        label: {
          en: 'Match widget availability',
          'nl-NL': 'Widget beschikbaarheid matchen',
        },
        type: 'boolean',
        required: false,
        defaultValue: false,
        adminOnly: true,
        description: {
          en: 'Cross-reference the consumer widget to detect blocked days not surfaced by the in-house API',
          'nl-NL':
            'Kruisverwijzing met de consumentenwidget om geblokkeerde dagen te detecteren die niet zichtbaar zijn via de in-house API',
        },
      },
    ],
  },
  mice: {
    displayName: { en: 'MICE', 'nl-NL': 'MICE' },
    description: {
      en: 'Connect to MICE to schedule events',
      'nl-NL': 'Verbind met MICE om evenementen in te plannen',
    },
    category: { en: 'Events', 'nl-NL': 'Evenementen' },
    status: 'live',
    iconPath: '/images/miceIcon.svg',
    configFields: [
      {
        key: 'api_key',
        label: { en: 'API Key', 'nl-NL': 'API Sleutel' },
        type: 'password',
        required: true,
        description: {
          en: 'Your MICE API key for authentication',
          'nl-NL': 'Uw MICE API sleutel voor authenticatie',
        },
      },
      {
        key: 'location_id',
        label: { en: 'Location ID', 'nl-NL': 'Locatie ID' },
        type: 'number',
        required: false,
        description: {
          en: 'Specific location ID if you have multiple venues',
          'nl-NL': 'Specifieke locatie ID als u meerdere locaties heeft',
        },
      },
    ],
  },
  whatsapp: {
    displayName: { en: 'WhatsApp', 'nl-NL': 'WhatsApp' },
    description: {
      en: 'Connect your WhatsApp Business Account to send and receive messages through this assistant.',
      'nl-NL': 'Verbind je WhatsApp Business Account om berichten te verzenden en ontvangen via deze assistent.',
    },
    category: { en: 'Messaging', 'nl-NL': 'Berichten' },
    status: 'live',
    iconPath: '/images/whatsappIcon.svg',
    configFields: [],
  },
  sms: {
    displayName: { en: 'SMS', 'nl-NL': 'SMS' },
    description: {
      en: 'Enables SMS to send messages to the caller',
      'nl-NL': 'Stelt SMS in om berichten te verzenden naar de aanroeper',
    },
    category: { en: 'Messaging', 'nl-NL': 'Berichten' },
    status: 'live',
    iconPath: '/images/smsIcon.svg',
    configFields: [],
  },
  mcp_custom: {
    displayName: { en: 'MCP Server (Custom)', 'nl-NL': 'MCP-server (Aangepast)' },
    description: {
      en: 'Connect an external Model Context Protocol server with dynamic configuration',
      'nl-NL': 'Verbind een externe Model Context Protocol-server met dynamische configuratie',
    },
    category: { en: 'External Tools', 'nl-NL': 'Externe tools' },
    status: 'beta',
    iconPath: '/images/mcp.svg',
    configFields: [],
  },
}

// List of integrable tool names for filtering
export const integrableToolNames = [
  'formitable',
  'zenchef',
  'fork',
  'guestplan',
  'tablefever',
  'gotable',
  'frank',
  'mice',
  'whatsapp',
  'sms',
  'mcp_custom',
  'opentable',
]
